import React, { useEffect, useMemo, useState } from "react";
import ActionButton from "../../components/ui/ActionButton";
import CommonDataTable from "../../components/ui/table/CommonTable";
import { useGetShiftsQuery } from "../../features/shifts/shiftApi";
import { getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const getShiftUid = (row) => row?.uid || row?._id || row?.id || row?.shift_id;

const ShiftTable = ({
  searchTerm = "",
  selectedPlantId = "",
  filters = {},
  onEditClick,
  onDeleteClick,
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, isFetching } = useGetShiftsQuery({
    page,
    limit: rowsPerPage,
    ...filters,
    plant_id: selectedPlantId,
  });
  const responseMetaData = useMemo(() => data?.meta_data || {}, [data?.meta_data]);

  useEffect(() => {
    setPage(1);
  }, [filters, searchTerm, selectedPlantId]);

  const rows = useMemo(() => {
    const tableRows = data?.data || [];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) return tableRows;

    return tableRows.filter((row) =>
      [
        row?.shift_id,
        row?.shift_name,
        row?.plant_id,
        getPlantLabel(row, responseMetaData),
        row?.start_time,
        row?.end_time,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedSearch))
    );
  }, [data?.data, responseMetaData, searchTerm]);

  const columns = [
    {
      name: "Actions",
      width: "132px",
      cell: (row) => (
        <div className="flex items-center justify-start gap-2">
          <ActionButton
            action="edit"
            iconOnly
            aria-label="Edit shift"
            onClick={() => onEditClick(row)}
          />

          <ActionButton
            action="delete"
            iconOnly
            aria-label="Delete shift"
            onClick={() => onDeleteClick(getShiftUid(row))}
          />
        </div>
      ),
    },
    { name: "Shift ID", selector: (row) => row?.shift_id || "-" },
    { name: "Shift Name", selector: (row) => row?.shift_name || "-" },
    {
      name: "Plant",
      selector: (row) => getPlantLabel(row, responseMetaData),
      cell: (row) => renderPlantCell(row, responseMetaData),
      grow: 2,
    },
    { name: "Start Time", selector: (row) => row?.start_time || "-" },
    { name: "End Time", selector: (row) => row?.end_time || "-" },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`rounded border px-2 py-1.5 text-xs font-medium ${
            row?.shift_status
              ? "border-green-500 bg-green-100 text-green-600"
              : "border-red-500 bg-red-100 text-red-600"
          }`}
        >
          {row?.shift_status ? "Active" : "Inactive"}
        </span>
      ),
    },
  ];

  return (
    <CommonDataTable
      columns={columns}
      data={rows}
      pagination
      paginationServer
      paginationTotalRows={data?.meta_data?.total_records || 0}
      paginationPerPage={rowsPerPage}
      filteredRowsCount={searchTerm ? rows.length : data?.meta_data?.total_records || 0}
      page={page}
      progressPending={isFetching}
      onChangePage={setPage}
      onChangeRowsPerPage={(newLimit) => {
        setRowsPerPage(newLimit);
        setPage(1);
      }}
    />
  );
};

export default ShiftTable;
